/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { usePgStores } from "../../stores/StoresProvider";
import { usePeopleCost, useHeadcountPlanning } from "../../hooks";
import { Icon, LoadingPulse } from "../../ui";
import { useIsMobile } from "../../hooks/useIsMobile";
import { defaultPhysicalStoreForRole } from "../../theme";

import { PeopleCostPanel } from "../schedule/scheduleComponents";

export function HeadcountPlanningPage({ user, lang }) {
  const isMobile = useIsMobile();
  const { storeNames, storeRecords } = usePgStores();
  const firstStore = defaultPhysicalStoreForRole(storeNames, user?.role);
  const [view, setView] = useState("store");
  const [selectedStore, setSelectedStore] = useState(
    user.store && user.store !== "All Stores" ? user.store : firstStore
  );

  useEffect(() => {
    setSelectedStore((prev) => {
      if (user.store && user.store !== "All Stores") return user.store;
      return storeNames.includes(prev) ? prev : firstStore;
    });
  }, [user.store, storeNames, firstStore]);
  const [scenario, setScenario] = useState({
    supervisor: 0,
    service: 0,
    kitchen: 0,
    cleaning: 0,
  });

  const selectedRecord = (storeRecords || []).find((r) => String(r.location_name || "").trim() === String(selectedStore || "").trim());
  const unifiedLocationId = view === "network" ? null : selectedRecord?.unified_location_id ?? null;
  const unifiedBrandId = view === "network" ? null : selectedRecord?.brand_id ?? null;
  const { data, loading } = useHeadcountPlanning(view === "network" ? null : selectedStore, unifiedLocationId, unifiedBrandId);
  const panelStore = selectedStore || firstStore;
  const panelMonday = (() => {
    const base = new Date();
    const day = base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - (day === 0 ? 6 : day - 1));
    monday.setHours(0, 0, 0, 0);
    return monday;
  })();
  const panelWeekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(panelMonday);
    d.setDate(panelMonday.getDate() + i);
    return d;
  });
  const panelWeekStartISO = `${panelMonday.getFullYear()}-${String(panelMonday.getMonth() + 1).padStart(2, "0")}-${String(panelMonday.getDate()).padStart(2, "0")}`;
  const panelRecord = (storeRecords || []).find((r) => String(r.location_name || "").trim() === String(panelStore || "").trim());
  const { data: panelPeopleCost, loading: panelPeopleCostLoading } = usePeopleCost(
    panelStore,
    panelWeekStartISO,
    0,
    panelRecord?.unified_location_id ?? null,
    panelRecord?.brand_id ?? null,
  );
  const storeData = view === "network" ? data?.network : data?.stores?.[selectedStore];
  const currentCapacity = storeData?.current_capacity;
  const monthlyData = storeData?.monthly_data || [];
  const actualsData = storeData?.actuals || [];
  const allMonths = [
    ...actualsData.map(a => ({ month: a.month, value: a.actual_headcount, type: "actual" })),
    ...monthlyData.map(m => ({ month: m.month, value: Number(m.headcount_needed || 0), type: "projection" }))
  ].sort((a, b) => (a.month || "").localeCompare(b.month || ""));
  const chartNow = new Date();
  const chartY = chartNow.getFullYear();
  const chartM = chartNow.getMonth();
  const chartMinDate = new Date(chartY, chartM - 6, 1);
  const chartMaxDate = new Date(chartY, chartM + 1, 1);
  const chartMinKey = `${chartMinDate.getFullYear()}-${String(chartMinDate.getMonth() + 1).padStart(2, "0")}`;
  const chartMaxKey = `${chartMaxDate.getFullYear()}-${String(chartMaxDate.getMonth() + 1).padStart(2, "0")}`;
  const chartMonths = allMonths.filter(
    item => item.month && item.month >= chartMinKey && item.month <= chartMaxKey
  );
  const latestMonth = monthlyData[0] || null;
  const baseMonthlyCost = Number(currentCapacity?.monthly_cost || 0);
  const avgRate = Number(currentCapacity?.avg_hourly_rate || 6.7);
  const avgWeeklyHrs = Number(currentCapacity?.weekly_hours || 0) / Math.max(Number(currentCapacity?.total || 0), 1);
  const deltaHeads = scenario.supervisor + scenario.service + scenario.kitchen + scenario.cleaning;
  const deltaMonthly = deltaHeads * avgWeeklyHrs * 4.33 * avgRate * 1.2415;
  const scenarioCost = baseMonthlyCost + deltaMonthly;
  const scenarioPct = latestMonth?.monthly_revenue > 0 ? (scenarioCost / latestMonth.monthly_revenue) * 100 : null;
  const basePct = latestMonth?.people_cost_pct ?? null;
  const pcTarget = Number(storeData?.people_cost_threshold_pct) || 22;
  const impactPct = scenarioPct !== null && basePct !== null ? scenarioPct - basePct : null;
  const anyScenario = deltaHeads !== 0;
  const capacityLine = Number(currentCapacity?.total || 0) + deltaHeads;
  const maxVal = Math.max(
    ...chartMonths.map(item => Number(item.value || 0)),
    capacityLine,
    1
  ) * 1.15;

  function pctColor(pct) {
    if (pct === null || pct === undefined) return "var(--text-muted)";
    const goodCeil = (pcTarget * 25) / 30;
    if (pct < goodCeil) return "var(--green)";
    if (pct <= pcTarget) return "#D97706";
    return "#DC2626";
  }

  function cardTint(group) {
    return {
      supervisor: { bg: "#F5F3FF", fg: "#6D28D9" },
      service: { bg: "#ECFDF5", fg: "#047857" },
      kitchen: { bg: "#FFFBEB", fg: "#B45309" },
      cleaning: { bg: "#EFF6FF", fg: "#1D4ED8" },
    }[group];
  }

  function setGroupDelta(group, value) {
    setScenario(prev => ({ ...prev, [group]: value }));
  }

  function bump(group, dir) {
    setScenario(prev => ({ ...prev, [group]: prev[group] + dir }));
  }

  function fmtMoney(v) {
    return `€${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }

  if (isMobile) return null;
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 56 }}>
        <LoadingPulse size={26} />
      </div>
    );
  }
  if (!storeData || !currentCapacity) {
    return (
      <div className="empty-state">
        <Icon name="chart" size={40} />
        <p>No headcount data available</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <div style={{ display: "flex", gap: 4, background: "var(--surface2)", padding: 4, borderRadius: 14, width: "fit-content" }}>
        <button
          onClick={() => setView("store")}
          style={{ padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 700, background: view === "store" ? "var(--green)" : "transparent", color: view === "store" ? "#fff" : "var(--text-secondary)" }}
        >
          {lang === "gr" ? "Ανά Κατάστημα" : "Per Store"}
        </button>
        <button
          onClick={() => setView("network")}
          style={{ padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 700, background: view === "network" ? "var(--green)" : "transparent", color: view === "network" ? "#fff" : "var(--text-secondary)" }}
        >
          {lang === "gr" ? "Όλα τα Καταστήματα" : "All Stores"}
        </button>
      </div>

      {view === "store" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {storeNames.map(s => (
            <button
              key={s}
              onClick={() => setSelectedStore(s)}
              style={{ padding: "7px 11px", borderRadius: 999, border: "1.5px solid var(--border)", background: selectedStore === s ? "var(--green-light)" : "var(--surface2)", color: selectedStore === s ? "var(--green)" : "var(--text-secondary)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 14 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>{lang === "gr" ? "Τρέχον Μηνιαίο Κόστος" : "Current Monthly Cost"}</div>
          <div style={{ fontFamily: "Bebas Neue,sans-serif", fontSize: 34, lineHeight: 1.05, color: "var(--text-primary)" }}>{fmtMoney(baseMonthlyCost)}</div>
        </div>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 14, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>{lang === "gr" ? "Προβλεπόμενο People Cost" : "Predicted People Cost"}</div>
          <div style={{ fontFamily: "Bebas Neue,sans-serif", fontSize: 42, lineHeight: 1, color: pctColor(scenarioPct) }}>
            {scenarioPct === null ? "—" : `${scenarioPct.toFixed(1)}%`}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{lang === "gr" ? `Στόχος: < ${pcTarget}%` : `Target: < ${pcTarget}%`}</div>
        </div>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 14 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>{lang === "gr" ? "Επίδραση Σεναρίου" : "Scenario Impact"}</div>
          {anyScenario ? (
            <>
              <div style={{ fontFamily: "Bebas Neue,sans-serif", fontSize: 34, lineHeight: 1.05, color: deltaMonthly > 0 ? "#DC2626" : "var(--green)" }}>
                {deltaMonthly >= 0 ? "+" : "-"}{fmtMoney(Math.abs(deltaMonthly))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: deltaMonthly > 0 ? "#DC2626" : "var(--green)" }}>
                {impactPct === null ? "—" : `${impactPct >= 0 ? "+" : ""}${impactPct.toFixed(1)}% people cost`}
              </div>
            </>
          ) : (
            <div style={{ marginTop: 16, fontSize: 12, color: "var(--text-muted)" }}>{lang === "gr" ? "Χωρίς αλλαγές" : "No scenario changes"}</div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
        {[
          { key: "supervisor", label: lang === "gr" ? "Supervisors" : "Supervisors" },
          { key: "service", label: lang === "gr" ? "Service Crew" : "Service Crew" },
          { key: "kitchen", label: lang === "gr" ? "Kitchen Staff" : "Kitchen Staff" },
          { key: "cleaning", label: lang === "gr" ? "Cleaning" : "Cleaning" },
        ].map(g => {
          const tint = cardTint(g.key);
          const base = Number(currentCapacity[g.key] || 0);
          const delta = Number(scenario[g.key] || 0);
          return (
            <div key={g.key} style={{ background: tint.bg, border: `1.5px solid ${tint.fg}22`, borderRadius: 14, padding: 12 }}>
              <div style={{ fontSize: 11, color: tint.fg, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>{g.label}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <div style={{ fontFamily: "Bebas Neue,sans-serif", fontSize: 34, lineHeight: 1, color: tint.fg }}>{base}</div>
                {delta !== 0 && <div style={{ fontSize: 12, fontWeight: 800, color: delta > 0 ? "#DC2626" : "var(--green)" }}>{delta > 0 ? `+${delta}` : delta}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10, color: "var(--text-primary)" }}>{lang === "gr" ? "Προσαρμογή Σεναρίου" : "Scenario Adjusters"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {[
            { key: "supervisor", label: lang === "gr" ? "Supervisors" : "Supervisors" },
            { key: "service", label: lang === "gr" ? "Service Crew" : "Service Crew" },
            { key: "kitchen", label: lang === "gr" ? "Kitchen Staff" : "Kitchen Staff" },
            { key: "cleaning", label: lang === "gr" ? "Cleaning Crew" : "Cleaning Crew" },
          ].map(group => (
            <div key={group.key} style={{ background: "var(--surface2)", borderRadius: 12, padding: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{group.label}</div>
                <button onClick={() => setGroupDelta(group.key, 0)} style={{ border: "none", background: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                  {lang === "gr" ? "Επαναφορά" : "Reset"}
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => bump(group.key, -1)} style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--surface)", cursor: "pointer", fontWeight: 900 }}>−</button>
                <div style={{ minWidth: 40, textAlign: "center", fontWeight: 800, fontSize: 16, color: scenario[group.key] > 0 ? "#DC2626" : scenario[group.key] < 0 ? "var(--green)" : "var(--text-muted)" }}>
                  {scenario[group.key] > 0 ? `+${scenario[group.key]}` : scenario[group.key]}
                </div>
                <button onClick={() => bump(group.key, 1)} style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--surface)", cursor: "pointer", fontWeight: 900 }}>+</button>
              </div>
            </div>
          ))}
        </div>
        {anyScenario && (
          <button
            onClick={() => setScenario({ supervisor: 0, service: 0, kitchen: 0, cleaning: 0 })}
            className="btn-base btn-green"
            style={{ marginTop: 12 }}
          >
            {lang === "gr" ? "Reset all" : "Reset all"}
          </button>
        )}
      </div>

      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 20, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4, color: "var(--text-primary)" }}>
          {lang === "gr" ? "Μηνιαίο απαιτούμενο προσωπικό vs τρέχουσα ομάδα" : "Monthly ideal coverage vs current capacity"}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>
          {lang === "gr" ? "Μπάρες = άτομα που χρειάζονται · γραμμή = τρέχουσα ομάδα" : "Bars = headcount needed · line = what your current team can cover"}
        </div>

        {(() => {
          const chartH = 320;
          const barW = 52;
          const gap = 32;
          const labelH = 28;
          const leftPad = 48;
          const totalW = leftPad + chartMonths.length * (barW + gap);
          const svgH = chartH + labelH + 8;
          const svgW = Math.max(totalW, 700);
          const yMax = Math.ceil(maxVal / 50) * 50 || 50;
          const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(f * yMax));
          const capacityY = chartH - (capacityLine / yMax) * chartH;
          const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const splitIndex = chartMonths.findIndex((item, idx) => item.type === "projection" && idx > 0 && chartMonths[idx - 1]?.type === "actual");
          const splitX = splitIndex > 0 ? leftPad + (splitIndex - 0.5 + 0.5) * (barW + gap) : null;

          return (
            <div style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch" }}>
              <svg
                viewBox={`0 0 ${svgW} ${svgH}`}
                width="100%"
                style={{ display: "block", maxWidth: "100%", height: "auto" }}
                preserveAspectRatio="xMinYMin meet"
              >
                {ticks.map(tick => {
                  const y = chartH - (tick / yMax) * chartH;
                  return (
                    <g key={tick}>
                      <line x1={leftPad} y1={y} x2={svgW} y2={y} stroke="var(--border)" strokeWidth={1} />
                      <text x={leftPad - 6} y={y + 4} textAnchor="end" fontSize={10} fill="var(--text-muted)" fontWeight={600}>{tick}</text>
                    </g>
                  );
                })}

                <line
                  x1={leftPad} y1={capacityY}
                  x2={svgW} y2={capacityY}
                  stroke="#818CF8" strokeWidth={1.5} strokeDasharray="5 4"
                />
                <text
                  x={svgW - 6} y={capacityY - 6}
                  textAnchor="end" fontSize={10} fill="#818CF8" fontWeight={700}
                >
                  {lang === "gr" ? `Ομάδα: ${capacityLine}` : `Team: ${capacityLine}`}
                </text>

                {splitX !== null && (
                  <g>
                    <line
                      x1={splitX}
                      y1={0}
                      x2={splitX}
                      y2={chartH}
                      stroke="var(--border)"
                      strokeWidth={1}
                      strokeDasharray="4 3"
                    />
                    <text
                      x={splitX}
                      y={10}
                      textAnchor="middle"
                      fontSize={9}
                      fill="var(--text-muted)"
                      fontWeight={700}
                    >
                      Actual | Forecast
                    </text>
                  </g>
                )}

                {chartMonths.map((m, i) => {
                  const value = Number(m.value || 0);
                  const barH = Math.max((value / yMax) * chartH, 4);
                  const x = leftPad + i * (barW + gap) + gap / 2;
                  const y = chartH - barH;
                  const monthLabel = m.month ? MONTH_NAMES[parseInt(m.month.split("-")[1], 10) - 1] || m.month : "";
                  const isActual = m.type === "actual";
                  const exceedsCapacity = value > capacityLine;
                  const fill = isActual ? "var(--surface2)" : (exceedsCapacity ? "#FBCFE8" : "#6EE7B7");
                  const stroke = isActual ? "var(--border)" : (exceedsCapacity ? "#F472B6" : "#34D399");
                  const labelInside = barH > 28;
                  const labelColor = isActual
                    ? "var(--text-muted)"
                    : (exceedsCapacity ? "#9D174D" : "#065F46");
                  return (
                    <g key={`${m.type}-${m.month}-${i}`}>
                      <rect x={x} y={y} width={barW} height={barH} fill={fill} stroke={stroke} strokeWidth={1} rx={6} ry={6} />
                      <text
                        x={x + barW / 2}
                        y={labelInside ? y + 16 : y - 5}
                        textAnchor="middle" fontSize={10} fontWeight={800}
                        fill={labelInside ? labelColor : "var(--text-muted)"}
                      >
                        {value.toFixed(1)}
                      </text>
                      <text x={x + barW / 2} y={chartH + 18} textAnchor="middle" fontSize={11} fill="var(--text-muted)" fontWeight={600}>
                        {monthLabel}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          );
        })()}

        <div style={{ marginTop: 14, display: "flex", gap: 20, flexWrap: "wrap", fontSize: 11, fontWeight: 700, width: "100%", maxWidth: "100%" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: "#6EE7B7", display: "inline-block" }} />
            <span style={{ color: "var(--text-muted)" }}>{lang === "gr" ? "Ζήτηση (εντός δυναμικότητας)" : "Ideal demand"}</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: "#FBCFE8", display: "inline-block" }} />
            <span style={{ color: "var(--text-muted)" }}>{lang === "gr" ? "Ζήτηση πάνω από δυναμικότητα" : "Demand exceeds capacity"}</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width={20} height={12} style={{ display: "inline-block" }}>
              <line x1={0} y1={6} x2={20} y2={6} stroke="#818CF8" strokeWidth={1.5} strokeDasharray="4 3" />
            </svg>
            <span style={{ color: "var(--text-muted)" }}>{lang === "gr" ? "Τρέχουσα ομάδα" : "Current capacity"}</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: "var(--surface2)", border: "1.5px solid var(--border)", display: "inline-block" }} />
            <span style={{ color: "var(--text-muted)" }}>{lang === "gr" ? "Πραγματικό headcount" : "Actual headcount"}</span>
          </span>
        </div>
      </div>

      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 14, marginTop: 16 }}>
        <PeopleCostPanel
          data={panelPeopleCost}
          loading={panelPeopleCostLoading}
          lang={lang}
          weekDates={panelWeekDates}
        />
      </div>
    </div>
  );
}

